import { removeInvalidZeros } from '@app/shared/utils/number-utils';
import { gte, chain, isNaN, replace, head, last } from 'lodash';

import {
  CurrencyFormatFn,
  ICurrency,
  ICurrencyFormatOptions,
} from './currency.types';

/**
 * common currency format function
 *
 * @param currency target currency instance
 * @param settings format setting
 */
const commonCurrencyFormat: CurrencyFormatFn = (
  currency: ICurrency,
  opts: ICurrencyFormatOptions,
): string => {
  const {
    isAddTrailZeros,
    defaultFormatValue,
    precision,
    pattern,
    negativePattern,
    symbol,
    thousandsSeparator,
    decimalSeparator,
    groups,
    minDecimalPlacesToCheck,
  } = opts;

  const isCurrencyNaN = isNaN(currency.value);

  return (
    chain(
      gte(isCurrencyNaN ? 0 : currency.value, 0) ? pattern : negativePattern,
    )
      // handle currency region
      .replace('!', symbol)
      // handle number region
      .replace('#', () => {
        // return directly when currency value is NaN
        if (isCurrencyNaN) {
          return defaultFormatValue;
        }

        let [dollars, cents] = chain(
          currency.toString(isAddTrailZeros ? precision : undefined),
        )
          .replace(/^-/, '')
          .split('.')
          .value();

        // avoid cents as string undefined
        const removeResult = cents
          ? removeInvalidZeros(
              `${dollars}.${cents}`,
              minDecimalPlacesToCheck,
            ).split('.')
          : [dollars, undefined];
        dollars = head(removeResult);
        cents = removeResult.length > 1 ? last(removeResult) : undefined;

        // combine dollars and cents with thousandsSeparator and decimal separator
        return (
          replace(dollars, groups, `$1${thousandsSeparator}`) +
          (cents ? `${decimalSeparator}${cents}` : '')
        );
      })
      .value()
  );
};

export { commonCurrencyFormat };
